// Lux Calculation & Rating System
// Formula: Lux = (Lumens × Efficiency Factor) / Area (m²)
// Efficiency Factor accounts for: lens loss (~0.85), surface reflectivity (~0.7-0.9), ambient loss
// Default: 0.7

const DEFAULT_EFFICIENCY = 0.70;

const ASPECT_RATIOS = {
  '16:9': 16 / 9, '16:10': 16 / 10, '4:3': 4 / 3,
  '21:9': 21 / 9, '1:1': 1, '2.35:1': 2.35
};

// Indoor Projection (Controlled Lighting)
const INDOOR_RATINGS = [
  { min: 300, label: 'Excellent', color: [0, 130, 60] },    // Dark Green
  { min: 200, label: 'Very Good', color: [0, 170, 70] },    // Green
  { min: 100, label: 'Good', color: [100, 180, 50] },       // Light Green
  { min: 50,  label: 'Acceptable', color: [220, 200, 0] },  // Yellow
  { min: 0,   label: 'Poor', color: [200, 50, 40] }         // Red
];

// Outdoor Projection (Ambient Light Present)
const OUTDOOR_RATINGS = [
  { min: 250, label: 'Excellent', color: [0, 130, 60] },    // Dark Green
  { min: 180, label: 'Very Good', color: [0, 170, 70] },    // Green
  { min: 140, label: 'Good', color: [100, 180, 50] },       // Light Green
  { min: 120, label: 'Average', color: [220, 200, 0] },     // Yellow
  { min: 80,  label: 'Low', color: [230, 140, 30] },        // Orange
  { min: 0,   label: 'Poor', color: [200, 50, 40] }         // Red
];

export function calculateLux(lumens, areaSqM, efficiency) {
  const eff = efficiency ?? DEFAULT_EFFICIENCY;
  return (lumens * eff) / Math.max(areaSqM, 0.01);
}

export function getProjectionArea(projector) {
  const ratio = ASPECT_RATIOS[projector.aspect_ratio] || ASPECT_RATIOS['16:10'];
  const throwDist = projector.throw_distance || 5;
  const throwRatio = projector.throw_ratio || 1.5;
  const imgWidthM = throwDist / throwRatio;
  const imgHeightM = imgWidthM / ratio;
  return { width: imgWidthM, height: imgHeightM, area: imgWidthM * imgHeightM };
}

export function getLuxRating(lux, projectType) {
  const ratings = projectType === 'outdoor' ? OUTDOOR_RATINGS : INDOOR_RATINGS;
  for (const r of ratings) {
    if (lux >= r.min) return r;
  }
  return ratings[ratings.length - 1];
}

// Stacking multiplier (top + bottom + left + right + 1 base). When stacking
// is enabled, effective lumens scale linearly — virtual brightness boost
// without rendering additional projections.
function getStackCount(p) {
  const s = p?.stacking;
  if (!s || !s.enabled) return 1;
  return 1 + (parseInt(s.top) || 0) + (parseInt(s.bottom) || 0) + (parseInt(s.left) || 0) + (parseInt(s.right) || 0);
}

export function getProjectorLux(projector, projectType, efficiency) {
  const baseLumens = projector.lumens || projector.spec?.brightness || 10000;
  const stackCount = getStackCount(projector);
  const lumens = baseLumens * stackCount;
  const { area } = getProjectionArea(projector);
  const lux = calculateLux(lumens, area, efficiency);
  const rating = getLuxRating(lux, projectType);
  return { lux: Math.round(lux), area, rating, lumens, baseLumens, stackCount };
}

// CSS color string from RGB array
export function luxColorCSS(rating) {
  return `rgb(${rating.color[0]}, ${rating.color[1]}, ${rating.color[2]})`;
}

export { INDOOR_RATINGS, OUTDOOR_RATINGS, DEFAULT_EFFICIENCY, ASPECT_RATIOS };
